import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { Button } from '../components/ui/button';
import { 
  Shield, 
  LayoutDashboard, 
  MessageSquare, 
  Briefcase, 
  FileText, 
  Search, 
  GraduationCap, 
  User, 
  LogOut,
  Menu,
  X,
  FileSearch,
  Moon,
  Sun,
  CreditCard,
  Users,
  AlertTriangle
} from 'lucide-react';
import { useState } from 'react';

const navItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
  { icon: Shield, label: 'Benefits Chat', path: '/benefits-chat' },
  { icon: Briefcase, label: 'Career Chat', path: '/career-chat' },
  { icon: User, label: 'My Profile', path: '/profile' },
  { icon: FileText, label: 'Documents', path: '/documents' },
  { icon: FileSearch, label: 'ATS Scorer', path: '/ats-scoring' },
  { icon: Search, label: 'Job Search', path: '/jobs' },
  { icon: GraduationCap, label: 'Learning', path: '/learning' },
  { icon: Users, label: 'Coaching', path: '/coaching' },
  { icon: MessageSquare, label: 'Community', path: '/community' },
  { icon: CreditCard, label: 'Membership', path: '/membership' },
];

const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const { isPaymentPastDue, isAccessSuspended, paymentStatus } = useSubscription();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-navy-50 dark:bg-navy-900 transition-colors duration-300">
      {/* Mobile header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white dark:bg-navy-800 border-b border-navy-100 dark:border-navy-700 px-4 py-3 transition-colors">
        <div className="flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2">
            <Shield className="w-7 h-7 text-amber-600" />
            <span className="font-heading font-bold text-navy-900 dark:text-white">Atlas</span>
          </Link>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="text-navy-600 dark:text-navy-300"
              data-testid="mobile-theme-toggle"
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              data-testid="mobile-menu-btn"
            >
              {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
          </div>
        </div>
      </header>

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full w-64 bg-navy-900 z-40 transform transition-transform duration-300
        lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-navy-700">
            <Link to="/dashboard" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="font-heading font-bold text-white">Atlas</p>
                <p className="text-xs text-navy-400">Career Solutions</p>
              </div>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-lg transition-all
                    ${isActive 
                      ? 'bg-amber-500 text-white' 
                      : 'text-navy-300 hover:bg-navy-800 hover:text-white'}
                  `}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-navy-700">
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="w-full flex items-center gap-3 px-4 py-3 mb-2 rounded-lg text-navy-300 hover:bg-navy-800 hover:text-white transition-colors"
              data-testid="theme-toggle"
            >
              {isDark ? (
                <>
                  <Sun className="w-5 h-5" />
                  <span className="font-medium">Light Mode</span>
                </>
              ) : (
                <>
                  <Moon className="w-5 h-5" />
                  <span className="font-medium">Dark Mode</span>
                </>
              )}
            </button>
            
            <div className="flex items-center gap-3 px-4 py-3 mb-2">
              <div className="w-10 h-10 bg-navy-700 rounded-full flex items-center justify-center overflow-hidden">
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                ) : (
                  <User className="w-5 h-5 text-navy-300" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium truncate">{user?.name}</p>
                <p className="text-navy-400 text-sm truncate">{user?.email}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              className="w-full justify-start text-navy-300 hover:text-white hover:bg-navy-800"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5 mr-3" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        {/* Payment Alert Banner */}
        {(isPaymentPastDue || isAccessSuspended) && (
          <div className={`px-4 py-3 ${
            isAccessSuspended 
              ? 'bg-red-500 text-white' 
              : 'bg-amber-500 text-white'
          }`}>
            <div className="flex items-center justify-between max-w-7xl mx-auto">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                <p className="text-sm font-medium">
                  {isAccessSuspended 
                    ? 'Your membership access is suspended. Update your payment to restore access.'
                    : 'Payment failed. Please update your payment method to avoid service interruption.'}
                </p>
              </div>
              <Link to="/membership">
                <Button 
                  size="sm" 
                  variant="secondary"
                  className="bg-white text-navy-900 hover:bg-navy-100"
                >
                  Update Payment
                </Button>
              </Link>
            </div>
          </div>
        )}
        {children}
      </main>
    </div>
  );
};

export default DashboardLayout;
